import React from 'react';
import styled, { useTheme } from 'styled-components';
import { TitleLarge, TitleMedium } from '../../../components/Texts';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import TextInput from '../../../components/Inputs/Text';
import ImageInput from '../../TokenSubmission/ImageInput';
import Button from '../../../components/Button';
import {
  useCreateBulkOrderMutation,
  useProcessCpsOrdersMutation,
} from '../../../redux/api/authorized/bulkOrders';
import { toast } from 'react-toastify';
import Selector from '../../../components/Inputs/Select';

const Container = styled.div`
  width: 100%;
  min-height: calc(100vh - 200px);
  display: flex;
  flex-direction: column;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 40px;
`;

const FormColumn = styled.div`
  display: flex;
  width: 100%;
  gap: 15px;
  flex-direction: column;
`;

const schema = yup
  .object({
    imageUpload: yup.string().required('Gift card image is required'),
    physicalImageUpload: yup.string().required('Physical image is required'),
    giftCardsQuantity: yup
      .number()
      .required('Gift cards quantity is required')
      .positive('Gift cards quantity must be positive')
      .integer('Gift cards quantity must be an integer'),
    tokenSymbol: yup.string().required('Token symbol is required'),
    tokenAmount: yup
      .number()
      .required('Token amount is required')
      .positive('Token amount must be positive'),
    recipientName: yup.string().required('Recipient name is required'),
    address1: yup.string().required('Address line 1 is required'),
    address2: yup.string(),
    city: yup.string().required('City is required'),
    state: yup.string().required('State is required'),
    zip: yup.string().required('Zip code is required'),
    country: yup.string().required('Country is required'),
    shipMethod: yup.string().required('Shipping method is required'),
  })
  .required();

const BulkOrder = () => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid, isSubmitting },
  } = useForm({
    mode: 'all',
    resolver: yupResolver(schema),
    defaultValues: {
      shipMethod: 'Ground',
    },
  });

  const [createBulkOrder] = useCreateBulkOrderMutation();
  const [processCpsOrders, { isLoading: isProcessing }] =
    useProcessCpsOrdersMutation();

  const onSubmit = async (data) => {
    try {
      // ImageInput returns base64 data URLs, so we send them as imageUpload/physicalImageUpload
      const payload = {
        imageUpload: data.imageUpload,
        physicalImageUpload: data.physicalImageUpload,
        giftCardsQuantity: data.giftCardsQuantity,
        tokenSymbol: data.tokenSymbol,
        tokenAmount: data.tokenAmount,
        recipientName: data.recipientName,
        address1: data.address1,
        address2: data.address2 || '',
        city: data.city,
        state: data.state,
        zip: data.zip,
        country: data.country,
        shipMethod: data.shipMethod,
      };
      const response = await createBulkOrder(payload);
      if (response?.error) {
        toast('There was an error creating the bulk order.', {
          type: 'error',
        });
      } else {
        toast('Bulk order created successfully.', {
          type: 'success',
        });
        reset();
      }
    } catch (error) {
      toast('There was an error creating the bulk order.', {
        type: 'error',
      });
    }
  };

  const handleProcessOrders = async () => {
    try {
      const response = await processCpsOrders();
      if (response?.error) {
        toast('There was an error processing CPS orders.', {
          type: 'error',
        });
      } else {
        toast('CPS orders processed successfully.', {
          type: 'success',
        });
      }
    } catch (error) {
      toast('There was an error processing CPS orders.', {
        type: 'error',
      });
    }
  };

  const theme = useTheme();

  return (
    <Container>
      <TitleLarge>Create Bulk Order</TitleLarge>
      <TitleMedium>Create a bulk order for physical gift cards</TitleMedium>
      <Button
        onClick={handleProcessOrders}
        loading={isProcessing}
        disabled={isProcessing}
        style={{ marginTop: 20, marginBottom: 20 }}
        variant='secondary'
      >
        Process CPS Orders
      </Button>
      <form
        onSubmit={handleSubmit(onSubmit)}
        style={{
          marginTop: 20,
          gap: 40,
          display: 'flex',
          flexDirection: 'column',
          padding: 24,
          borderRadius: 16,
          border: `1px solid ${theme.border.primary}`,
        }}
      >
        <FormContainer>
          <FormColumn>
            <Controller
              name='imageUpload'
              control={control}
              render={({ field }) => (
                <ImageInput
                  noCrop={true}
                  aspectRatio={1028 / 570}
                  label='Gift card image (1028 × 570)'
                  value={field.value}
                  onChange={field.onChange}
                  error={errors?.imageUpload?.message}
                />
              )}
            />
            <Controller
              name='physicalImageUpload'
              control={control}
              render={({ field }) => (
                <ImageInput
                  noCrop={true}
                  aspectRatio={2000 / 1307}
                  label='Physical image (2000 × 1307)'
                  value={field.value}
                  onChange={field.onChange}
                  error={errors?.physicalImageUpload?.message}
                />
              )}
            />
            <Controller
              name='giftCardsQuantity'
              control={control}
              render={({ field }) => (
                <TextInput
                  label='Gift cards quantity'
                  type='number'
                  error={errors?.giftCardsQuantity?.message}
                  {...field}
                  onChange={(e) =>
                    field.onChange(parseInt(e.target.value) || '')
                  }
                />
              )}
            />
            <Controller
              name='tokenSymbol'
              control={control}
              render={({ field }) => (
                <TextInput
                  label='Token symbol'
                  type='text'
                  error={errors?.tokenSymbol?.message}
                  {...field}
                />
              )}
            />
            <Controller
              name='tokenAmount'
              control={control}
              render={({ field }) => (
                <TextInput
                  label='Token amount'
                  type='number'
                  step='any'
                  error={errors?.tokenAmount?.message}
                  {...field}
                  onChange={(e) =>
                    field.onChange(parseFloat(e.target.value) || '')
                  }
                />
              )}
            />
          </FormColumn>
          <FormColumn>
            <Controller
              name='recipientName'
              control={control}
              render={({ field }) => (
                <TextInput
                  label='Recipient name'
                  type='text'
                  error={errors?.recipientName?.message}
                  {...field}
                />
              )}
            />
            <Controller
              name='address1'
              control={control}
              render={({ field }) => (
                <TextInput
                  label='Address line 1'
                  type='text'
                  error={errors?.address1?.message}
                  {...field}
                />
              )}
            />
            <Controller
              name='address2'
              control={control}
              render={({ field }) => (
                <TextInput
                  label='Address line 2 (optional)'
                  type='text'
                  error={errors?.address2?.message}
                  {...field}
                />
              )}
            />
            <Controller
              name='city'
              control={control}
              render={({ field }) => (
                <TextInput
                  label='City'
                  type='text'
                  error={errors?.city?.message}
                  {...field}
                />
              )}
            />
            <Controller
              name='state'
              control={control}
              render={({ field }) => (
                <TextInput
                  label='State'
                  type='text'
                  error={errors?.state?.message}
                  {...field}
                />
              )}
            />
            <Controller
              name='zip'
              control={control}
              render={({ field }) => (
                <TextInput
                  label='Zip code'
                  type='text'
                  error={errors?.zip?.message}
                  {...field}
                />
              )}
            />
            <Controller
              name='country'
              control={control}
              render={({ field }) => (
                <TextInput
                  label='Country'
                  type='text'
                  error={errors?.country?.message}
                  {...field}
                />
              )}
            />
            <Controller
              name='shipMethod'
              control={control}
              render={({ field }) => (
                <Selector
                  label='Shipping method'
                  options={[
                    { label: 'UPS Next Day Air Early', value: 'NDAE' },
                    { label: 'UPS Next Day Air', value: 'NDA' },
                    { label: 'UPS Next Day Air Saver', value: 'NDAS' },
                    { label: 'UPS 2nd Day Air AM', value: '2DAM' },
                    { label: 'UPS 2nd Day Air', value: '2DA' },
                    { label: 'UPS 3 Day Select', value: '3DS' },
                    { label: 'UPS Ground', value: 'Ground' },
                    { label: 'UPS Worldwide Express Plus', value: 'WWEP' },
                    { label: 'UPS Worldwide Express', value: 'WWEX' },
                    { label: 'UPS Worldwide Saver', value: 'WWS' },
                    { label: 'UPS Worldwide Economy', value: 'WWEC' },
                  ]}
                  error={errors?.shipMethod?.message}
                  {...field}
                />
              )}
            />
          </FormColumn>
        </FormContainer>
        <Button loading={isSubmitting} disabled={!isValid || isSubmitting}>
          Create Bulk Order
        </Button>
      </form>
    </Container>
  );
};

export default BulkOrder;
